/**
 * Media Library Utility
 * Manages shared media assets for SMS, comments, and general CRM use
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc,
  deleteDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  limit,
  Unsubscribe,
  increment,
  DocumentData,
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject,
  UploadTaskSnapshot 
} from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { MediaItem, MediaType, MediaFolder } from '../types';

const MEDIA_COLLECTION = 'mediaLibrary';
const FOLDERS_COLLECTION = 'mediaFolders';
const STORAGE_PATH = 'media-library';

/** Firestore rejects `undefined` field values */
function omitUndefinedValues<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
}

/** `ref(storage, httpsDownloadUrl)` is invalid — extract object path from a Firebase download URL. */
function storagePathFromDownloadUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const m = u.pathname.match(/\/o\/(.+)$/);
    if (!m?.[1]) return null;
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

// =============================================================================
// Media Item Operations
// =============================================================================

/**
 * Upload a file to the media library
 */
export async function uploadMediaItem(
  file: File,
  options: {
    userId: string;
    userName: string;
    tags?: string[];
    userTags?: string[];
    labels?: string[];
    description?: string;
    folderId?: string;
    onProgress?: (progress: number) => void;
  }
): Promise<MediaItem> {
  const { userId, userName, tags = [], userTags = [], labels = [], description, onProgress } = options;

  await auth.authStateReady();
  if (!auth.currentUser) {
    throw new Error('You must be signed in to upload media.');
  }
  if (auth.currentUser.isAnonymous) {
    throw new Error('Anonymous sessions cannot upload to the media library. Sign in with Google.');
  }

  // Generate unique filename
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filename = `${timestamp}-${safeName}`;
  const storagePath = `${STORAGE_PATH}/${filename}`;
  
  // Determine media type (pass filename for 3D model detection)
  const type = getMediaType(file.type, file.name);
  
  // Create storage reference
  const storageRef = ref(storage, storagePath);
  
  // Upload file with progress tracking
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  
  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(progress);
      },
      (error) => {
        console.error('Upload error:', error);
        reject(error);
      },
      async () => {
        try {
          // Get download URL
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Create media item document
          const mediaItem: MediaItem = {
            name: file.name,
            url,
            storagePath,
            type,
            mimeType: file.type,
            size: file.size,
            uploadedBy: userId,
            uploadedByName: userName,
            tags,
            userTags,
            labels,
            usageCount: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          if (description !== undefined) {
            mediaItem.description = description;
          }
          
          // Generate thumbnail for images/videos (client-side for images)
          if (type === 'image') {
            const thumbnailUrl = await generateImageThumbnail(file);
            if (thumbnailUrl) {
              mediaItem.thumbnailUrl = thumbnailUrl;
            }
            
            // Get image dimensions
            const dimensions = await getImageDimensions(file);
            if (dimensions) {
              mediaItem.width = dimensions.width;
              mediaItem.height = dimensions.height;
            }
          }
          
          // Save to Firestore
          const docRef = doc(collection(db, MEDIA_COLLECTION));
          mediaItem.id = docRef.id;
          await setDoc(docRef, omitUndefinedValues(mediaItem as unknown as Record<string, unknown>) as DocumentData);
          
          resolve(mediaItem);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
}

/**
 * Get all media items
 */
export async function getAllMediaItems(options?: {
  type?: MediaType;
  tags?: string[];
  labels?: string[];
  searchQuery?: string;
  limitCount?: number;
}): Promise<MediaItem[]> {
  try {
    const mediaRef = collection(db, MEDIA_COLLECTION);
    let q = query(mediaRef, orderBy('createdAt', 'desc'));
    
    if (options?.type) {
      q = query(mediaRef, where('type', '==', options.type), orderBy('createdAt', 'desc'));
    }
    
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }
    
    const snapshot = await getDocs(q);
    
    let items: MediaItem[] = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
      lastUsedAt: doc.data().lastUsedAt?.toDate?.(),
    })) as MediaItem[];
    
    // Client-side filtering for tags, labels, and search
    if (options?.tags && options.tags.length > 0) {
      items = items.filter((item) =>
        options.tags!.some((tag) => item.tags.includes(tag))
      );
    }
    
    if (options?.labels && options.labels.length > 0) {
      items = items.filter((item) =>
        options.labels!.some((label) => item.labels.includes(label))
      );
    }
    
    if (options?.searchQuery) {
      const query = options.searchQuery.toLowerCase();
      items = items.filter((item) =>
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        (item.tags || []).some((tag) => tag.toLowerCase().includes(query)) ||
        (item.htmlModelDescriptors || []).some((d) => d.toLowerCase().includes(query)) ||
        (item.htmlPreviewImageUrl && item.htmlPreviewImageUrl.toLowerCase().includes(query))
      );
    }
    
    return items;
  } catch (error) {
    console.error('Error getting media items:', error);
    return [];
  }
}

/**
 * Get a single media item by ID
 */
export async function getMediaItem(id: string): Promise<MediaItem | null> {
  try {
    const docRef = doc(db, MEDIA_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.() || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || new Date(),
      lastUsedAt: data.lastUsedAt?.toDate?.(),
    } as MediaItem;
  } catch (error) {
    console.error('Error getting media item:', error);
    return null;
  }
}

/**
 * Update a media item
 */
export async function updateMediaItem(
  id: string,
  updates: Partial<Pick<MediaItem, 'name' | 'tags' | 'userTags' | 'labels' | 'description' | 'htmlModelDescriptors' | 'htmlPreviewImageUrl' | 'thumbnailUrl'>>
): Promise<void> {
  try {
    const docRef = doc(db, MEDIA_COLLECTION, id);
    await updateDoc(
      docRef,
      omitUndefinedValues({
        ...updates,
        updatedAt: new Date(),
      } as Record<string, unknown>) as DocumentData
    );
  } catch (error) {
    console.error('Error updating media item:', error);
    throw error;
  }
}

/**
 * Rename a media item
 */
export async function renameMediaItem(id: string, newName: string): Promise<void> {
  try {
    const docRef = doc(db, MEDIA_COLLECTION, id);
    await updateDoc(docRef, {
      name: newName,
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Error renaming media item:', error);
    throw error;
  }
}

/**
 * Delete a media item
 */
export async function deleteMediaItem(id: string): Promise<void> {
  try {
    // Get the media item to find the storage URL
    const item = await getMediaItem(id);
    if (!item) {
      throw new Error('Media item not found');
    }
    
    // Delete from storage
    try {
      const path = item.storagePath || storagePathFromDownloadUrl(item.url) || item.url;
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
    } catch (storageError) {
      console.warn('Could not delete from storage:', storageError);
      // Continue with Firestore deletion even if storage deletion fails
    }
    
    // Delete from Firestore
    const docRef = doc(db, MEDIA_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting media item:', error);
    throw error;
  }
}

/**
 * Track media usage (increment usage count)
 */
export async function trackMediaUsage(id: string): Promise<void> {
  try {
    const docRef = doc(db, MEDIA_COLLECTION, id);
    await updateDoc(docRef, {
      usageCount: increment(1),
      lastUsedAt: new Date(),
    });
  } catch (error) {
    console.error('Error tracking media usage:', error);
  }
}

/**
 * Subscribe to media items (real-time updates)
 */
export function subscribeToMediaItems(
  onUpdate: (items: MediaItem[]) => void,
  onError?: (error: Error) => void,
  options?: { type?: MediaType; limitCount?: number }
): Unsubscribe {
  const mediaRef = collection(db, MEDIA_COLLECTION);
  let q = query(mediaRef, orderBy('createdAt', 'desc'));
  
  if (options?.type) {
    q = query(mediaRef, where('type', '==', options.type), orderBy('createdAt', 'desc'));
  }
  
  if (options?.limitCount) {
    q = query(q, limit(options.limitCount));
  }
  
  return onSnapshot(
    q,
    (snapshot) => {
      const items: MediaItem[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        lastUsedAt: doc.data().lastUsedAt?.toDate?.(),
      })) as MediaItem[];
      onUpdate(items);
    },
    (error) => {
      console.error('Error in media subscription:', error);
      onError?.(error);
    }
  );
}

const MEDIA_CATALOG_LIMIT = 2500;

/** Media library rows that are HTML / WebGL viewers (model type + html mime or extension). */
export function isCatalogHtmlMediaItem(item: Pick<MediaItem, 'mimeType' | 'name' | 'type'>): boolean {
  if (item.type !== 'model') return false;
  const m = (item.mimeType || '').toLowerCase();
  if (m === 'text/html' || m === 'application/xhtml+xml') return true;
  const n = (item.name || '').toLowerCase();
  return n.endsWith('.html') || n.endsWith('.htm');
}

/** Recent media rows for the global HTML model catalog (bounded snapshot). */
export function subscribeRecentMediaItemsForCatalog(
  onUpdate: (items: MediaItem[]) => void,
  onError?: (error: Error) => void,
  maxDocs: number = MEDIA_CATALOG_LIMIT,
): Unsubscribe {
  const mediaRef = collection(db, MEDIA_COLLECTION);
  const q = query(mediaRef, orderBy('createdAt', 'desc'), limit(maxDocs));
  return onSnapshot(
    q,
    (snapshot) => {
      const items: MediaItem[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
        lastUsedAt: doc.data().lastUsedAt?.toDate?.(),
      })) as MediaItem[];
      onUpdate(items);
    },
    (error) => {
      console.error('[subscribeRecentMediaItemsForCatalog]', error);
      onError?.(error);
    },
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Determine media type from MIME type or file extension
 */
export function getMediaType(mimeType: string, fileName?: string): MediaType {
  if (mimeType.startsWith('image/')) {
    return 'image';
  }
  if (mimeType.startsWith('video/')) {
    return 'video';
  }
  
  // Check for 3D model files by extension (MIME types may not be recognized)
  if (fileName) {
    const ext = fileName.toLowerCase().split('.').pop();
    if (['sat', 'step', 'stp', 'iges', 'igs', 'stl', 'obj', '3ds', 'fbx', 'dae', 'gltf', 'glb', 'html', 'htm'].includes(ext || '')) {
      return 'model';
    }
  }
  if (mimeType === 'text/html') return 'model';
  
  // Also check MIME type for 3D models
  if (mimeType.includes('model') || mimeType.includes('acis') || mimeType.includes('step') || mimeType.includes('iges')) {
    return 'model';
  }
  
  return 'document';
}

/**
 * Get file icon based on MIME type or filename
 */
export function getFileIcon(mimeType: string, fileName?: string): string {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎥';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📑';
  
  // Check for 3D model files by extension
  if (fileName) {
    const ext = fileName.toLowerCase().split('.').pop();
    if (['sat', 'step', 'stp', 'iges', 'igs', 'stl', 'obj', '3ds', 'fbx', 'dae', 'gltf', 'glb', 'html', 'htm'].includes(ext || '')) {
      return '🧊';
    }
  }
  if (mimeType === 'text/html') return '🧊';
  
  // Also check MIME type for 3D models
  if (mimeType.includes('model') || mimeType.includes('acis') || mimeType.includes('step') || mimeType.includes('iges')) {
    return '🧊';
  }
  
  return '📎';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * Generate image thumbnail (data URL)
 */
async function generateImageThumbnail(file: File, maxSize = 200): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);
      
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Get image dimensions
 */
async function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Check if file type is supported for preview
 */
export function canPreview(mimeType: string): boolean {
  return (
    mimeType.startsWith('image/') ||
    mimeType.startsWith('video/') ||
    mimeType === 'application/pdf' ||
    mimeType === 'text/html'
  );
}

/**
 * Get all unique tags from media items
 */
export async function getAllTags(): Promise<string[]> {
  try {
    const items = await getAllMediaItems();
    const tagSet = new Set<string>();
    items.forEach((item) => {
      item.tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  } catch (error) {
    console.error('Error getting all tags:', error);
    return [];
  }
}

/**
 * Get all unique labels from media items
 */
export async function getAllLabels(): Promise<string[]> {
  try {
    const items = await getAllMediaItems();
    const labelSet = new Set<string>();
    items.forEach((item) => {
      item.labels.forEach((label) => labelSet.add(label));
    });
    return Array.from(labelSet).sort();
  } catch (error) {
    console.error('Error getting all labels:', error);
    return [];
  }
}
